/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const N = globalThis, V = N.ShadowRoot && (N.ShadyCSS === void 0 || N.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, F = Symbol(), et = /* @__PURE__ */ new WeakMap();
let ht = class {
  constructor(t, e, o) {
    if (this._$cssResult$ = !0, o !== F) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (V && t === void 0) {
      const o = e !== void 0 && e.length === 1;
      o && (t = et.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), o && et.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const bt = (r) => new ht(typeof r == "string" ? r : r + "", void 0, F), kt = (r, ...t) => {
  const e = r.length === 1 ? r[0] : t.reduce(((o, i, n) => o + ((a) => {
    if (a._$cssResult$ === !0) return a.cssText;
    if (typeof a == "number") return a;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + a + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + r[n + 1]), r[0]);
  return new ht(e, r, F);
}, gt = (r, t) => {
  if (V) r.adoptedStyleSheets = t.map(((e) => e instanceof CSSStyleSheet ? e : e.styleSheet));
  else for (const e of t) {
    const o = document.createElement("style"), i = N.litNonce;
    i !== void 0 && o.setAttribute("nonce", i), o.textContent = e.cssText, r.appendChild(o);
  }
}, ot = V ? (r) => r : (r) => r instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const o of t.cssRules) e += o.cssText;
  return bt(e);
})(r) : r;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: wt, defineProperty: yt, getOwnPropertyDescriptor: $t, getOwnPropertyNames: xt, getOwnPropertySymbols: _t, getPrototypeOf: At } = Object, D = globalThis, rt = D.trustedTypes, Et = rt ? rt.emptyScript : "", Ct = D.reactiveElementPolyfillSupport, x = (r, t) => r, j = { toAttribute(r, t) {
  switch (t) {
    case Boolean:
      r = r ? Et : null;
      break;
    case Object:
    case Array:
      r = r == null ? r : JSON.stringify(r);
  }
  return r;
}, fromAttribute(r, t) {
  let e = r;
  switch (t) {
    case Boolean:
      e = r !== null;
      break;
    case Number:
      e = r === null ? null : Number(r);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(r);
      } catch {
        e = null;
      }
  }
  return e;
} }, J = (r, t) => !wt(r, t), it = { attribute: !0, type: String, converter: j, reflect: !1, useDefault: !1, hasChanged: J };
Symbol.metadata ??= Symbol("metadata"), D.litPropertyMetadata ??= /* @__PURE__ */ new WeakMap();
let w = class extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ??= []).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = it) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(t) && ((e = Object.create(e)).wrapped = !0), this.elementProperties.set(t, e), !e.noAccessor) {
      const o = Symbol(), i = this.getPropertyDescriptor(t, o, e);
      i !== void 0 && yt(this.prototype, t, i);
    }
  }
  static getPropertyDescriptor(t, e, o) {
    const { get: i, set: n } = $t(this.prototype, t) ?? { get() {
      return this[e];
    }, set(a) {
      this[e] = a;
    } };
    return { get: i, set(a) {
      const d = i?.call(this);
      n?.call(this, a), this.requestUpdate(t, d, o);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? it;
  }
  static _$Ei() {
    if (this.hasOwnProperty(x("elementProperties"))) return;
    const t = At(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(x("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(x("properties"))) {
      const e = this.properties, o = [...xt(e), ..._t(e)];
      for (const i of o) this.createProperty(i, e[i]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0) for (const [o, i] of e) this.elementProperties.set(o, i);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, o] of this.elementProperties) {
      const i = this._$Eu(e, o);
      i !== void 0 && this._$Eh.set(i, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const o = new Set(t.flat(1 / 0).reverse());
      for (const i of o) e.unshift(ot(i));
    } else t !== void 0 && e.push(ot(t));
    return e;
  }
  static _$Eu(t, e) {
    const o = e.attribute;
    return o === !1 ? void 0 : typeof o == "string" ? o : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    this._$ES = new Promise(((t) => this.enableUpdating = t)), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), this.constructor.l?.forEach(((t) => t(this)));
  }
  addController(t) {
    (this._$EO ??= /* @__PURE__ */ new Set()).add(t), this.renderRoot !== void 0 && this.isConnected && t.hostConnected?.();
  }
  removeController(t) {
    this._$EO?.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const o of e.keys()) this.hasOwnProperty(o) && (t.set(o, this[o]), delete this[o]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return gt(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    this.renderRoot ??= this.createRenderRoot(), this.enableUpdating(!0), this._$EO?.forEach(((t) => t.hostConnected?.()));
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    this._$EO?.forEach(((t) => t.hostDisconnected?.()));
  }
  attributeChangedCallback(t, e, o) {
    this._$AK(t, o);
  }
  _$ET(t, e) {
    const o = this.constructor.elementProperties.get(t), i = this.constructor._$Eu(t, o);
    if (i !== void 0 && o.reflect === !0) {
      const n = (o.converter?.toAttribute !== void 0 ? o.converter : j).toAttribute(e, o.type);
      this._$Em = t, n == null ? this.removeAttribute(i) : this.setAttribute(i, n), this._$Em = null;
    }
  }
  _$AK(t, e) {
    const o = this.constructor, i = o._$Eh.get(t);
    if (i !== void 0 && this._$Em !== i) {
      const n = o.getPropertyOptions(i), a = typeof n.converter == "function" ? { fromAttribute: n.converter } : n.converter?.fromAttribute !== void 0 ? n.converter : j;
      this._$Em = i;
      const d = a.fromAttribute(e, n.type);
      this[i] = d ?? this._$Ej?.get(i) ?? d, this._$Em = null;
    }
  }
  requestUpdate(t, e, o) {
    if (t !== void 0) {
      const i = this.constructor, n = this[t];
      if (o ??= i.getPropertyOptions(t), !((o.hasChanged ?? J)(n, e) || o.useDefault && o.reflect && n === this._$Ej?.get(t) && !this.hasAttribute(i._$Eu(t, o)))) return;
      this.C(t, e, o);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(t, e, { useDefault: o, reflect: i, wrapped: n }, a) {
    o && !(this._$Ej ??= /* @__PURE__ */ new Map()).has(t) && (this._$Ej.set(t, a ?? e ?? this[t]), n !== !0 || a !== void 0) || (this._$AL.has(t) || (this.hasUpdated || o || (e = void 0), this._$AL.set(t, e)), i === !0 && this._$Em !== t && (this._$Eq ??= /* @__PURE__ */ new Set()).add(t));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ??= this.createRenderRoot(), this._$Ep) {
        for (const [i, n] of this._$Ep) this[i] = n;
        this._$Ep = void 0;
      }
      const o = this.constructor.elementProperties;
      if (o.size > 0) for (const [i, n] of o) {
        const { wrapped: a } = n, d = this[i];
        a !== !0 || this._$AL.has(i) || d === void 0 || this.C(i, void 0, n, d);
      }
    }
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), this._$EO?.forEach(((o) => o.hostUpdate?.())), this.update(e)) : this._$EM();
    } catch (o) {
      throw t = !1, this._$EM(), o;
    }
    t && this._$AE(e);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    this._$EO?.forEach(((e) => e.hostUpdated?.())), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Eq &&= this._$Eq.forEach(((e) => this._$ET(e, this[e]))), this._$EM();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
};
w.elementStyles = [], w.shadowRootOptions = { mode: "open" }, w[x("elementProperties")] = /* @__PURE__ */ new Map(), w[x("finalized")] = /* @__PURE__ */ new Map(), Ct?.({ ReactiveElement: w }), (D.reactiveElementVersions ??= []).push("2.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const K = globalThis, L = K.trustedTypes, nt = L ? L.createPolicy("lit-html", { createHTML: (r) => r }) : void 0, ut = "$lit$", f = `lit$${Math.random().toFixed(9).slice(2)}$`, pt = "?" + f, St = `<${pt}>`, g = document, E = () => g.createComment(""), C = (r) => r === null || typeof r != "object" && typeof r != "function", X = Array.isArray, zt = (r) => X(r) || typeof r?.[Symbol.iterator] == "function", G = `[ 	
\f\r]`, $ = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, at = /-->/g, st = />/g, m = RegExp(`>|${G}(?:([^\\s"'>=/]+)(${G}*=${G}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), dt = /'/g, lt = /"/g, ft = /^(?:script|style|textarea|title)$/i, Pt = (r) => (t, ...e) => ({ _$litType$: r, strings: t, values: e }), T = Pt(1), v = Symbol.for("lit-noChange"), c = Symbol.for("lit-nothing"), ct = /* @__PURE__ */ new WeakMap(), k = g.createTreeWalker(g, 129);
function vt(r, t) {
  if (!X(r) || !r.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return nt !== void 0 ? nt.createHTML(t) : t;
}
const Ht = (r, t) => {
  const e = r.length - 1, o = [];
  let i, n = t === 2 ? "<svg>" : t === 3 ? "<math>" : "", a = $;
  for (let d = 0; d < e; d++) {
    const s = r[d];
    let h, b, l = -1, u = 0;
    for (; u < s.length && (a.lastIndex = u, b = a.exec(s), b !== null); ) u = a.lastIndex, a === $ ? b[1] === "!--" ? a = at : b[1] !== void 0 ? a = st : b[2] !== void 0 ? (ft.test(b[2]) && (i = RegExp("</" + b[2], "g")), a = m) : b[3] !== void 0 && (a = m) : a === m ? b[0] === ">" ? (a = i ?? $, l = -1) : b[1] === void 0 ? l = -2 : (l = a.lastIndex - b[2].length, h = b[1], a = b[3] === void 0 ? m : b[3] === '"' ? lt : dt) : a === lt || a === dt ? a = m : a === at || a === st ? a = $ : (a = m, i = void 0);
    const p = a === m && r[d + 1].startsWith("/>") ? " " : "";
    n += a === $ ? s + St : l >= 0 ? (o.push(h), s.slice(0, l) + ut + s.slice(l) + f + p) : s + f + (l === -2 ? d : p);
  }
  return [vt(r, n + (r[e] || "<?>") + (t === 2 ? "</svg>" : t === 3 ? "</math>" : "")), o];
};
class S {
  constructor({ strings: t, _$litType$: e }, o) {
    let i;
    this.parts = [];
    let n = 0, a = 0;
    const d = t.length - 1, s = this.parts, [h, b] = Ht(t, e);
    if (this.el = S.createElement(h, o), k.currentNode = this.el.content, e === 2 || e === 3) {
      const l = this.el.content.firstChild;
      l.replaceWith(...l.childNodes);
    }
    for (; (i = k.nextNode()) !== null && s.length < d; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) for (const l of i.getAttributeNames()) if (l.endsWith(ut)) {
          const u = b[a++], p = i.getAttribute(l).split(f), O = /([.?@])?(.*)/.exec(u);
          s.push({ type: 1, index: n, name: O[2], strings: p, ctor: O[1] === "." ? Ot : O[1] === "?" ? Tt : O[1] === "@" ? Rt : I }), i.removeAttribute(l);
        } else l.startsWith(f) && (s.push({ type: 6, index: n }), i.removeAttribute(l));
        if (ft.test(i.tagName)) {
          const l = i.textContent.split(f), u = l.length - 1;
          if (u > 0) {
            i.textContent = L ? L.emptyScript : "";
            for (let p = 0; p < u; p++) i.append(l[p], E()), k.nextNode(), s.push({ type: 2, index: ++n });
            i.append(l[u], E());
          }
        }
      } else if (i.nodeType === 8) if (i.data === pt) s.push({ type: 2, index: n });
      else {
        let l = -1;
        for (; (l = i.data.indexOf(f, l + 1)) !== -1; ) s.push({ type: 7, index: n }), l += f.length - 1;
      }
      n++;
    }
  }
  static createElement(t, e) {
    const o = g.createElement("template");
    return o.innerHTML = t, o;
  }
}
function y(r, t, e = r, o) {
  if (t === v) return t;
  let i = o !== void 0 ? e._$Co?.[o] : e._$Cl;
  const n = C(t) ? void 0 : t._$litDirective$;
  return i?.constructor !== n && (i?._$AO?.(!1), n === void 0 ? i = void 0 : (i = new n(r), i._$AT(r, e, o)), o !== void 0 ? (e._$Co ??= [])[o] = i : e._$Cl = i), i !== void 0 && (t = y(r, i._$AS(r, t.values), i, o)), t;
}
class Mt {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: e }, parts: o } = this._$AD, i = (t?.creationScope ?? g).importNode(e, !0);
    k.currentNode = i;
    let n = k.nextNode(), a = 0, d = 0, s = o[0];
    for (; s !== void 0; ) {
      if (a === s.index) {
        let h;
        s.type === 2 ? h = new z(n, n.nextSibling, this, t) : s.type === 1 ? h = new s.ctor(n, s.name, s.strings, this, t) : s.type === 6 && (h = new Ut(n, this, t)), this._$AV.push(h), s = o[++d];
      }
      a !== s?.index && (n = k.nextNode(), a++);
    }
    return k.currentNode = g, i;
  }
  p(t) {
    let e = 0;
    for (const o of this._$AV) o !== void 0 && (o.strings !== void 0 ? (o._$AI(t, o, e), e += o.strings.length - 2) : o._$AI(t[e])), e++;
  }
}
class z {
  get _$AU() {
    return this._$AM?._$AU ?? this._$Cv;
  }
  constructor(t, e, o, i) {
    this.type = 2, this._$AH = c, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = o, this.options = i, this._$Cv = i?.isConnected ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && t?.nodeType === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = y(this, t, e), C(t) ? t === c || t == null || t === "" ? (this._$AH !== c && this._$AR(), this._$AH = c) : t !== this._$AH && t !== v && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : zt(t) ? this.k(t) : this._(t);
  }
  O(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  _(t) {
    this._$AH !== c && C(this._$AH) ? this._$AA.nextSibling.data = t : this.T(g.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    const { values: e, _$litType$: o } = t, i = typeof o == "number" ? this._$AC(t) : (o.el === void 0 && (o.el = S.createElement(vt(o.h, o.h[0]), this.options)), o);
    if (this._$AH?._$AD === i) this._$AH.p(e);
    else {
      const n = new Mt(i, this), a = n.u(this.options);
      n.p(e), this.T(a), this._$AH = n;
    }
  }
  _$AC(t) {
    let e = ct.get(t.strings);
    return e === void 0 && ct.set(t.strings, e = new S(t)), e;
  }
  k(t) {
    X(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let o, i = 0;
    for (const n of t) i === e.length ? e.push(o = new z(this.O(E()), this.O(E()), this, this.options)) : o = e[i], o._$AI(n), i++;
    i < e.length && (this._$AR(o && o._$AB.nextSibling, i), e.length = i);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    for (this._$AP?.(!1, !0, e); t !== this._$AB; ) {
      const o = t.nextSibling;
      t.remove(), t = o;
    }
  }
  setConnected(t) {
    this._$AM === void 0 && (this._$Cv = t, this._$AP?.(t));
  }
}
class I {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, e, o, i, n) {
    this.type = 1, this._$AH = c, this._$AN = void 0, this.element = t, this.name = e, this._$AM = i, this.options = n, o.length > 2 || o[0] !== "" || o[1] !== "" ? (this._$AH = Array(o.length - 1).fill(new String()), this.strings = o) : this._$AH = c;
  }
  _$AI(t, e = this, o, i) {
    const n = this.strings;
    let a = !1;
    if (n === void 0) t = y(this, t, e, 0), a = !C(t) || t !== this._$AH && t !== v, a && (this._$AH = t);
    else {
      const d = t;
      let s, h;
      for (t = n[0], s = 0; s < n.length - 1; s++) h = y(this, d[o + s], e, s), h === v && (h = this._$AH[s]), a ||= !C(h) || h !== this._$AH[s], h === c ? t = c : t !== c && (t += (h ?? "") + n[s + 1]), this._$AH[s] = h;
    }
    a && !i && this.j(t);
  }
  j(t) {
    t === c ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Ot extends I {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === c ? void 0 : t;
  }
}
class Tt extends I {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== c);
  }
}
class Rt extends I {
  constructor(t, e, o, i, n) {
    super(t, e, o, i, n), this.type = 5;
  }
  _$AI(t, e = this) {
    if ((t = y(this, t, e, 0) ?? c) === v) return;
    const o = this._$AH, i = t === c && o !== c || t.capture !== o.capture || t.once !== o.once || t.passive !== o.passive, n = t !== c && (o === c || i);
    i && this.element.removeEventListener(this.name, this, o), n && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    typeof this._$AH == "function" ? this._$AH.call(this.options?.host ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class Ut {
  constructor(t, e, o) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = o;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    y(this, t);
  }
}
const Nt = K.litHtmlPolyfillSupport;
Nt?.(S, z), (K.litHtmlVersions ??= []).push("3.3.1");
const jt = (r, t, e) => {
  const o = e?.renderBefore ?? t;
  let i = o._$litPart$;
  if (i === void 0) {
    const n = e?.renderBefore ?? null;
    o._$litPart$ = i = new z(t.insertBefore(E(), n), n, void 0, e ?? {});
  }
  return i._$AI(r), i;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Y = globalThis;
let _ = class extends w {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    const t = super.createRenderRoot();
    return this.renderOptions.renderBefore ??= t.firstChild, t;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = jt(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    super.connectedCallback(), this._$Do?.setConnected(!0);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._$Do?.setConnected(!1);
  }
  render() {
    return v;
  }
};
_._$litElement$ = !0, _.finalized = !0, Y.litElementHydrateSupport?.({ LitElement: _ });
const Lt = Y.litElementPolyfillSupport;
Lt?.({ LitElement: _ });
(Y.litElementVersions ??= []).push("4.2.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Bt = { attribute: !0, type: String, converter: j, reflect: !1, hasChanged: J }, Dt = (r = Bt, t, e) => {
  const { kind: o, metadata: i } = e;
  let n = globalThis.litPropertyMetadata.get(i);
  if (n === void 0 && globalThis.litPropertyMetadata.set(i, n = /* @__PURE__ */ new Map()), o === "setter" && ((r = Object.create(r)).wrapped = !0), n.set(e.name, r), o === "accessor") {
    const { name: a } = e;
    return { set(d) {
      const s = t.get.call(this);
      t.set.call(this, d), this.requestUpdate(a, s, r);
    }, init(d) {
      return d !== void 0 && this.C(a, void 0, r, d), d;
    } };
  }
  if (o === "setter") {
    const { name: a } = e;
    return function(d) {
      const s = this[a];
      t.call(this, d), this.requestUpdate(a, s, r);
    };
  }
  throw Error("Unsupported decorator location: " + o);
};
function P(r) {
  return (t, e) => typeof e == "object" ? Dt(r, t, e) : ((o, i, n) => {
    const a = i.hasOwnProperty(n);
    return i.constructor.createProperty(n, o), a ? Object.getOwnPropertyDescriptor(i, n) : void 0;
  })(r, t, e);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Z = { ATTRIBUTE: 1, CHILD: 2 }, Q = (r) => (...t) => ({ _$litDirective$: r, values: t });
class tt {
  constructor(t) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(t, e, o) {
    this._$Ct = t, this._$AM = e, this._$Ci = o;
  }
  _$AS(t, e) {
    return this.update(t, e);
  }
  update(t, e) {
    return this.render(...e);
  }
}
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const It = Q(class extends tt {
  constructor(r) {
    if (super(r), r.type !== Z.ATTRIBUTE || r.name !== "class" || r.strings?.length > 2) throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(r) {
    return " " + Object.keys(r).filter(((t) => r[t])).join(" ") + " ";
  }
  update(r, [t]) {
    if (this.st === void 0) {
      this.st = /* @__PURE__ */ new Set(), r.strings !== void 0 && (this.nt = new Set(r.strings.join(" ").split(/\s/).filter(((o) => o !== ""))));
      for (const o in t) t[o] && !this.nt?.has(o) && this.st.add(o);
      return this.render(t);
    }
    const e = r.element.classList;
    for (const o of this.st) o in t || (e.remove(o), this.st.delete(o));
    for (const o in t) {
      const i = !!t[o];
      i === this.st.has(o) || this.nt?.has(o) || (i ? (e.add(o), this.st.add(o)) : (e.remove(o), this.st.delete(o)));
    }
    return v;
  }
});
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Gt = (r) => r.strings === void 0;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const A = (r, t) => {
  const e = r._$AN;
  if (e === void 0) return !1;
  for (const o of e) o._$AO?.(t, !1), A(o, t);
  return !0;
}, B = (r) => {
  let t, e;
  do {
    if ((t = r._$AM) === void 0) break;
    e = t._$AN, e.delete(r), r = t;
  } while (e?.size === 0);
}, mt = (r) => {
  for (let t; t = r._$AM; r = t) {
    let e = t._$AN;
    if (e === void 0) t._$AN = e = /* @__PURE__ */ new Set();
    else if (e.has(r)) break;
    e.add(r), Vt(t);
  }
};
function qt(r) {
  this._$AN !== void 0 ? (B(this), this._$AM = r, mt(this)) : this._$AM = r;
}
function Wt(r, t = !1, e = 0) {
  const o = this._$AH, i = this._$AN;
  if (i !== void 0 && i.size !== 0) if (t) if (Array.isArray(o)) for (let n = e; n < o.length; n++) A(o[n], !1), B(o[n]);
  else o != null && (A(o, !1), B(o));
  else A(this, r);
}
const Vt = (r) => {
  r.type == Z.CHILD && (r._$AP ??= Wt, r._$AQ ??= qt);
};
class Ft extends tt {
  constructor() {
    super(...arguments), this._$AN = void 0;
  }
  _$AT(t, e, o) {
    super._$AT(t, e, o), mt(this), this.isConnected = t._$AU;
  }
  _$AO(t, e = !0) {
    t !== this.isConnected && (this.isConnected = t, t ? this.reconnected?.() : this.disconnected?.()), e && (A(this, t), B(this));
  }
  setValue(t) {
    if (Gt(this._$Ct)) this._$Ct._$AI(t, this);
    else {
      const e = [...this._$Ct._$AH];
      e[this._$Ci] = t, this._$Ct._$AI(e, this, 0);
    }
  }
  disconnected() {
  }
  reconnected() {
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const R = () => new Jt();
class Jt {
}
const q = /* @__PURE__ */ new WeakMap(), U = Q(class extends Ft {
  render(r) {
    return c;
  }
  update(r, [t]) {
    const e = t !== this.G;
    return e && this.G !== void 0 && this.rt(void 0), (e || this.lt !== this.ct) && (this.G = t, this.ht = r.options?.host, this.rt(this.ct = r.element)), c;
  }
  rt(r) {
    if (this.isConnected || (r = void 0), typeof this.G == "function") {
      const t = this.ht ?? globalThis;
      let e = q.get(t);
      e === void 0 && (e = /* @__PURE__ */ new WeakMap(), q.set(t, e)), e.get(this.G) !== void 0 && this.G.call(this.ht, void 0), e.set(this.G, r), r !== void 0 && this.G.call(this.ht, r);
    } else this.G.value = r;
  }
  get lt() {
    return typeof this.G == "function" ? q.get(this.ht ?? globalThis)?.get(this.G) : this.G?.value;
  }
  disconnected() {
    this.lt === this.ct && this.rt(void 0);
  }
  reconnected() {
    this.rt(this.ct);
  }
});
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class W extends tt {
  constructor(t) {
    if (super(t), this.it = c, t.type !== Z.CHILD) throw Error(this.constructor.directiveName + "() can only be used in child bindings");
  }
  render(t) {
    if (t === c || t == null) return this._t = void 0, this.it = t;
    if (t === v) return t;
    if (typeof t != "string") throw Error(this.constructor.directiveName + "() called with a non-string value");
    if (t === this.it) return this._t;
    this.it = t;
    const e = [t];
    return e.raw = e, this._t = { _$litType$: this.constructor.resultType, strings: e, values: [] };
  }
}
W.directiveName = "unsafeHTML", W.resultType = 1;
const Kt = Q(W), Xt = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.3628 5.63727 5.63733 18.3627m12.72547.0L5.63733 5.63727" stroke="currentColor" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="square"/></svg>', Yt = '/*! tailwindcss v4.1.13 | MIT License | https://tailwindcss.com */@layer properties{@supports (((-webkit-hyphens:none)) and (not (margin-trim:inline))) or ((-moz-orient:inline) and (not (color:rgb(from red r g b)))){*,:before,:after,::backdrop{--tw-rotate-x:initial;--tw-rotate-y:initial;--tw-rotate-z:initial;--tw-skew-x:initial;--tw-skew-y:initial;--tw-border-style:solid;--tw-ease:initial;--tw-shadow:0 0 #0000;--tw-shadow-color:initial;--tw-shadow-alpha:100%;--tw-inset-shadow:0 0 #0000;--tw-inset-shadow-color:initial;--tw-inset-shadow-alpha:100%;--tw-ring-color:initial;--tw-ring-shadow:0 0 #0000;--tw-inset-ring-color:initial;--tw-inset-ring-shadow:0 0 #0000;--tw-ring-inset:initial;--tw-ring-offset-width:0px;--tw-ring-offset-color:#fff;--tw-ring-offset-shadow:0 0 #0000}}}@layer base{*,:after,:before{box-sizing:border-box;border:0;margin:0;padding:0}html{-webkit-text-size-adjust:100%;tab-size:4;-webkit-font-feature-settings:normal;font-feature-settings:normal;font-variation-settings:normal;font-family:Helvetica,Arial,sans-serif;line-height:1.5}body{line-height:inherit;margin:0}hr{height:0;color:inherit;border-top-width:1px}abbr:where([title]){border-bottom:none;-webkit-text-decoration:underline dotted;text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:inherit}a{color:inherit;-webkit-text-decoration:inherit;text-decoration:inherit}b,strong{font-weight:500}code,kbd,pre,samp{font-family:monospace;font-size:1em}small{font-size:80%}sub,sup{vertical-align:initial;font-size:75%;line-height:0;position:relative}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;font-weight:inherit;line-height:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}[type=button],[type=reset],[type=submit],button{-webkit-appearance:button;background-color:initial;background-image:none}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:initial}::-webkit-inner-spin-button{height:auto}::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dd,dl,figure,h1,h2,h3,h4,h5,h6,hr,p,pre,fieldset{margin:0}fieldset,legend{padding:0}menu,ol,ul{margin:0;padding:0;list-style:none}textarea{resize:vertical}input::-webkit-input-placeholder{opacity:1;color:#6c6765}textarea::-webkit-input-placeholder{opacity:1;color:#6c6765}:is(input:-ms-placeholder-shown,textarea:-ms-placeholder-shown),:is(input:placeholder-shown,textarea:placeholder-shown){opacity:1;color:#6c6765}input::-moz-placeholder{opacity:1;color:#6c6765}textarea::-moz-placeholder{opacity:1;color:#6c6765}input::placeholder,textarea::placeholder{opacity:1;color:#6c6765}[role=button],button{cursor:pointer;color:inherit}:disabled{cursor:default}audio,canvas,embed,iframe,img,object,svg,video{vertical-align:middle;display:block}img,video{max-width:100%;height:auto}template,[hidden]{display:none}}:root,:host{--ease-in-out:cubic-bezier(.4,0,.2,1);--default-transition-duration:.15s;--default-transition-timing-function:cubic-bezier(.4,0,.2,1)}.sr-only{clip-path:inset(50%);white-space:nowrap;border-width:0;width:1px;height:1px;margin:-1px;padding:0;position:absolute;overflow:hidden}.fixed{position:fixed}.relative{position:relative}.static{position:static}.inset-0{inset:var(--bdk-space-0,0rem)}.z-10{z-index:10}.z-20{z-index:20}.m-1{margin:var(--bdk-space-1,.666667rem)}.mb-2{margin-bottom:var(--bdk-space-2,1.33333rem)}.bdk-tw-button-icon{--bdk-button-icon-size-extra-extra-small:var(--bdk-space-6,4rem);--bdk-button-icon-size-extra-small:var(--bdk-space-8,5.33333rem);--bdk-button-icon-size-small:var(--bdk-space-11,7.33333rem);--bdk-button-icon-size-medium:var(--bdk-space-14,9.33333rem);--bdk-button-icon-size-large:var(--bdk-space-16,10.6667rem);--bdk-button-icon-stroke-width-extra-extra-small:1;--bdk-button-icon-stroke-width-extra-small:1.25;--bdk-button-icon-stroke-width-small:1.5;--bdk-button-icon-stroke-width-medium:1.75;--bdk-button-icon-stroke-width-large:2;--bdk-button-icon-radius-pill:var(--bdk-radius-pill,166.5rem);--bdk-button-icon-radius-rounded:var(--bdk-radius-2,1.33333rem);--bdk-button-icon-radius:var(--bdk-button-icon-radius-rounded);--bdk-button-icon-default-border-width:1px;--bdk-button-icon-just-icon-border-width:0px;--bdk-button-icon-border-width:var(--bdk-button-icon-default-border-width);width:var(--bdk-button-icon-size);aspect-ratio:1;border-width:var(--bdk-button-icon-border-width);text-align:center;cursor:pointer;border-radius:var(--bdk-button-icon-radius);transition:background-color var(--bdk-transition-duration-default,.2s)ease,color var(--bdk-transition-duration-default,.2s)ease,border-color var(--bdk-transition-duration-default,.2s)ease;border-style:solid;justify-content:center;align-items:center;display:inline-flex}.block{display:block}.flex{display:flex}.h-\\[48px\\]{height:48px}.h-\\[800px\\]{height:800px}.h-full{height:100%}.w-\\[48px\\]{width:48px}.w-full{width:100%}.transform{transform:var(--tw-rotate-x,)var(--tw-rotate-y,)var(--tw-rotate-z,)var(--tw-skew-x,)var(--tw-skew-y,)}.bdk-tw-button-icon[variant=primary]:disabled{cursor:auto;background-color:var(--bdk-coal-100,#1a1a1a)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=primary]:disabled{background-color:color-mix(in srgb,var(--bdk-coal-100,#1a1a1a)25%,transparent)}}.bdk-tw-button-icon[variant=primary]:disabled{color:var(--bdk-snow-100,#fff);border-color:#0000}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=primary]:disabled{color:color-mix(in srgb,var(--bdk-snow-100,#fff)50%,transparent)}}.bdk-tw-button-icon[variant=highlight]:disabled{cursor:auto;background-color:var(--bdk-red-hover-light,#cb0d33);border-color:var(--bdk-red-hover-light,#cb0d33);color:var(--bdk-snow-100,#fff)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=highlight]:disabled{color:color-mix(in srgb,var(--bdk-snow-100,#fff)50%,transparent)}}.bdk-tw-button-icon[variant=highlight][inverted]:disabled{cursor:auto;background-color:var(--bdk-red-hover-light,#cb0d33);border-color:var(--bdk-red-hover-light,#cb0d33);color:var(--bdk-snow-100,#fff)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=highlight][inverted]:disabled{color:color-mix(in srgb,var(--bdk-snow-100,#fff)50%,transparent)}}.bdk-tw-button-icon[variant=outline]:disabled{cursor:auto;border-color:var(--bdk-coal-100,#1a1a1a);background-color:#0000}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=outline]:disabled{border-color:color-mix(in srgb,var(--bdk-coal-100,#1a1a1a)25%,transparent)}}.bdk-tw-button-icon[variant=outline]:disabled{color:var(--bdk-coal-100,#1a1a1a)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=outline]:disabled{color:color-mix(in srgb,var(--bdk-coal-100,#1a1a1a)25%,transparent)}}.bdk-tw-button-icon[variant=outline][inverted]:disabled{cursor:auto;border-color:var(--bdk-snow-100,#fff);background-color:#0000}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=outline][inverted]:disabled{border-color:color-mix(in srgb,var(--bdk-snow-100,#fff)25%,transparent)}}.bdk-tw-button-icon[variant=outline][inverted]:disabled{color:var(--bdk-snow-100,#fff)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=outline][inverted]:disabled{color:color-mix(in srgb,var(--bdk-snow-100,#fff)25%,transparent)}}.bdk-tw-button-icon[variant=overlay-neutral]:disabled{cursor:auto;background-color:var(--bdk-alabaster,#faf8f7);border-color:var(--bdk-alabaster,#faf8f7);color:var(--bdk-coal-100,#1a1a1a)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=overlay-neutral]:disabled{color:color-mix(in srgb,var(--bdk-coal-100,#1a1a1a)25%,transparent)}}.bdk-tw-button-icon[variant=overlay-light]:disabled{cursor:auto;background-color:var(--bdk-snow-100,#fff)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=overlay-light]:disabled{background-color:color-mix(in srgb,var(--bdk-snow-100,#fff)50%,transparent)}}.bdk-tw-button-icon[variant=overlay-light]:disabled{color:var(--bdk-coal-100,#1a1a1a);border-color:#0000}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=overlay-light]:disabled{color:color-mix(in srgb,var(--bdk-coal-100,#1a1a1a)25%,transparent)}}.bdk-tw-button-icon[variant=overlay-dark]:disabled{cursor:auto;background-color:var(--bdk-coal-100,#1a1a1a)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=overlay-dark]:disabled{background-color:color-mix(in srgb,var(--bdk-coal-100,#1a1a1a)25%,transparent)}}.bdk-tw-button-icon[variant=overlay-dark]:disabled{color:var(--bdk-snow-100,#fff)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=overlay-dark]:disabled{color:color-mix(in srgb,var(--bdk-snow-100,#fff)50%,transparent)}}.bdk-tw-button-icon[variant=just-icon]:disabled{cursor:auto;color:var(--bdk-coal-100,#1a1a1a)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=just-icon]:disabled{color:color-mix(in srgb,var(--bdk-coal-100,#1a1a1a)25%,transparent)}}.bdk-tw-button-icon[variant=just-icon][inverted]:disabled{cursor:auto;color:var(--bdk-snow-100,#fff)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=just-icon][inverted]:disabled{color:color-mix(in srgb,var(--bdk-snow-100,#fff)25%,transparent)}}.cursor-default{cursor:default}.flex-col{flex-direction:column}.flex-nowrap{flex-wrap:nowrap}.flex-wrap{flex-wrap:wrap}.items-center{align-items:center}.items-start{align-items:flex-start}.justify-between{justify-content:space-between}.justify-center{justify-content:center}.gap-4{gap:var(--bdk-space-4,2.66667rem)}.overflow-hidden{overflow:hidden}.rounded-2{border-radius:var(--bdk-radius-2,1.33333rem)}.border{border-style:var(--tw-border-style);border-width:1px}.bdk-tw-button-icon[variant=primary]{background-color:var(--bdk-coal-100,#1a1a1a);border-color:var(--bdk-coal-100,#1a1a1a);color:var(--bdk-snow-100,#fff)}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=primary]:hover{background-color:var(--bdk-red-hover-light,#cb0d33);border-color:var(--bdk-red-hover-light,#cb0d33);color:var(--bdk-snow-100,#fff)}}.bdk-tw-button-icon[variant=primary][inverted]{background-color:var(--bdk-snow-100,#fff);border-color:var(--bdk-snow-100,#fff);color:var(--bdk-coal-100,#1a1a1a)}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=primary][inverted]:hover{background-color:var(--bdk-red-hover-light,#cb0d33);border-color:var(--bdk-red-hover-light,#cb0d33);color:var(--bdk-snow-100,#fff)}}.bdk-tw-button-icon[variant=primary][inverted]:disabled{color:var(--bdk-snow-100,#fff)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=primary][inverted]:disabled{color:color-mix(in srgb,var(--bdk-snow-100,#fff)25%,transparent)}}.bdk-tw-button-icon[variant=primary][inverted]:disabled{background-color:#ffffff40;border-color:#0000}.bdk-tw-button-icon[variant=highlight]{background-color:var(--bdk-red-hover-light,#cb0d33);border-color:var(--bdk-red-hover-light,#cb0d33);color:var(--bdk-snow-100,#fff)}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=highlight][inverted]:hover{background-color:var(--bdk-snow-100,#fff);border-color:var(--bdk-snow-100,#fff);color:var(--bdk-coal-100,#1a1a1a)}}.bdk-tw-button-icon[variant=outline]{border-color:var(--bdk-coal-100,#1a1a1a);color:var(--bdk-coal-100,#1a1a1a);background-color:#0000}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=outline]:hover{background-color:var(--bdk-coal-100,#1a1a1a);border-color:var(--bdk-coal-100,#1a1a1a);color:var(--bdk-snow-100,#fff)}}.bdk-tw-button-icon[variant=outline][inverted]{border-color:var(--bdk-snow-100,#fff);color:var(--bdk-snow-100,#fff);background-color:#0000}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=outline][inverted]:hover{background-color:var(--bdk-snow-100,#fff);border-color:var(--bdk-snow-100,#fff);color:var(--bdk-coal-100,#1a1a1a)}}.bdk-tw-button-icon[variant=overlay-neutral]{background-color:var(--bdk-concrete,#f4f2f2);border-color:var(--bdk-concrete,#f4f2f2);color:var(--bdk-coal-100,#1a1a1a)}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=overlay-neutral]:hover{background-color:var(--bdk-coal-100,#1a1a1a);border-color:var(--bdk-coal-100,#1a1a1a);color:var(--bdk-snow-100,#fff)}}.bdk-tw-button-icon[variant=overlay-light]{background-color:var(--bdk-snow-100,#fff)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=overlay-light]{background-color:color-mix(in srgb,var(--bdk-snow-100,#fff)50%,transparent)}}.bdk-tw-button-icon[variant=overlay-light]{color:var(--bdk-coal-100,#1a1a1a);border-color:#0000}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=overlay-light]:hover{background-color:var(--bdk-coal-100,#1a1a1a);border-color:var(--bdk-coal-100,#1a1a1a);color:var(--bdk-snow-100,#fff)}}.bdk-tw-button-icon[variant=overlay-dark]{background-color:var(--bdk-coal-100,#1a1a1a)}@supports (color:color-mix(in lab,red,red)){.bdk-tw-button-icon[variant=overlay-dark]{background-color:color-mix(in srgb,var(--bdk-coal-100,#1a1a1a)50%,transparent)}}.bdk-tw-button-icon[variant=overlay-dark]{color:var(--bdk-snow-100,#fff);border-color:#0000}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=highlight]:hover{background-color:var(--bdk-coal-100,#1a1a1a);border-color:var(--bdk-coal-100,#1a1a1a)}.bdk-tw-button-icon[variant=overlay-dark]:hover{color:var(--bdk-coal-100,#1a1a1a);background-color:var(--bdk-snow-100,#fff)}}.bdk-tw-button-icon[variant=just-icon]{--bdk-button-icon-border-width:var(--bdk-button-icon-just-icon-border-width);background-color:#0000}.bdk-tw-button-icon svg path{stroke-width:var(--bdk-button-icon-stroke-width)}.p-0{padding:var(--bdk-space-0,0rem)}.text-left{text-align:left}.bdk-tw-button-icon[size=extra-extra-small]{--bdk-button-icon-size:var(--bdk-button-icon-size-extra-extra-small);--bdk-button-icon-stroke-width:var(--bdk-button-icon-stroke-width-extra-extra-small);font-family:var(--bdk-button-s-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-button-s-font-size,2rem);font-weight:var(--bdk-button-s-font-weight,500);letter-spacing:var(--bdk-button-s-letter-spacing,.033333rem);line-height:var(--bdk-button-s-line-height,3rem);-webkit-text-decoration:var(--bdk-button-s-text-decoration,none);text-decoration:var(--bdk-button-s-text-decoration,none);text-transform:var(--bdk-button-s-text-transform,uppercase)}.bdk-tw-button-icon[size=extra-small]{--bdk-button-icon-size:var(--bdk-button-icon-size-extra-small);--bdk-button-icon-stroke-width:var(--bdk-button-icon-stroke-width-extra-small);font-family:var(--bdk-button-s-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-button-s-font-size,2rem);font-weight:var(--bdk-button-s-font-weight,500);letter-spacing:var(--bdk-button-s-letter-spacing,.033333rem);line-height:var(--bdk-button-s-line-height,3rem);-webkit-text-decoration:var(--bdk-button-s-text-decoration,none);text-decoration:var(--bdk-button-s-text-decoration,none);text-transform:var(--bdk-button-s-text-transform,uppercase)}.bdk-tw-button-icon[size=small]{--bdk-button-icon-size:var(--bdk-button-icon-size-small);--bdk-button-icon-stroke-width:var(--bdk-button-icon-stroke-width-small);font-family:var(--bdk-button-s-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-button-s-font-size,2rem);font-weight:var(--bdk-button-s-font-weight,500);letter-spacing:var(--bdk-button-s-letter-spacing,.033333rem);line-height:var(--bdk-button-s-line-height,3rem);-webkit-text-decoration:var(--bdk-button-s-text-decoration,none);text-decoration:var(--bdk-button-s-text-decoration,none);text-transform:var(--bdk-button-s-text-transform,uppercase)}.bdk-tw-button-icon[size=medium]{--bdk-button-icon-size:var(--bdk-button-icon-size-medium);--bdk-button-icon-stroke-width:var(--bdk-button-icon-stroke-width-medium);font-family:var(--bdk-button-m-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-button-m-font-size,2.33333rem);font-weight:var(--bdk-button-m-font-weight,500);letter-spacing:var(--bdk-button-m-letter-spacing,.033333rem);line-height:var(--bdk-button-m-line-height,3.66667rem);-webkit-text-decoration:var(--bdk-button-m-text-decoration,none);text-decoration:var(--bdk-button-m-text-decoration,none);text-transform:var(--bdk-button-m-text-transform,uppercase)}.bdk-tw-button-icon[size=large]{--bdk-button-icon-size:var(--bdk-button-icon-size-large);--bdk-button-icon-stroke-width:var(--bdk-button-icon-stroke-width-large);font-family:var(--bdk-button-l-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-button-l-font-size,2.66667rem);font-weight:var(--bdk-button-l-font-weight,500);letter-spacing:var(--bdk-button-l-letter-spacing,.05rem);line-height:var(--bdk-button-l-line-height,4rem);-webkit-text-decoration:var(--bdk-button-l-text-decoration,none);text-decoration:var(--bdk-button-l-text-decoration,none);text-transform:var(--bdk-button-l-text-transform,uppercase)}.bdk-tw-heading-m{letter-spacing:.083333rem;text-transform:uppercase;font-family:Eduard Breuninger;font-size:3.33333rem;font-weight:500;line-height:5rem}.bdk-tw-button-icon[variant=just-icon][inverted]{--bdk-button-icon-border-width:var(--bdk-button-icon-just-icon-border-width);color:var(--bdk-snow-100,#fff)}@media screen and (pointer:fine){.bdk-tw-button-icon[variant=just-icon]:hover,.bdk-tw-button-icon[variant=just-icon][inverted]:hover{color:var(--bdk-red-hover-light,#cb0d33)}}.bdk-tw-button-icon:not(:disabled):focus-visible{outline:2px solid var(--bdk-focus-color);outline-offset:2px;box-shadow:0 0 0 3px var(--bdk-focus-background-color)}.bdk-tw-button-icon:not(:disabled)[inverted]:focus-visible{outline:2px solid var(--bdk-focus-color-inverted);outline-offset:2px;box-shadow:0 0 0 3px var(--bdk-focus-background-color-inverted)}.transition{transition-property:color,background-color,border-color,outline-color,text-decoration-color,fill,stroke,--tw-gradient-from,--tw-gradient-via,--tw-gradient-to,opacity,box-shadow,transform,translate,scale,rotate,filter,-webkit-backdrop-filter,backdrop-filter,display,content-visibility,overlay,pointer-events;transition-timing-function:var(--tw-ease,var(--default-transition-timing-function));transition-duration:var(--tw-duration,var(--default-transition-duration))}.ease-in-out{--tw-ease:var(--ease-in-out);transition-timing-function:var(--ease-in-out)}@media (prefers-reduced-motion:reduce){.bdk-tw-button-icon{transition:none}}.bdk-tw-button-icon[circle]{--bdk-button-icon-radius:var(--bdk-button-icon-radius-pill)}@media (hover:hover){.hover\\:text-red-hover-light:hover{color:var(--bdk-red-hover-light,#cb0d33)}}.focus\\:ring-2:focus{--tw-ring-shadow:var(--tw-ring-inset,)0 0 0 calc(2px + var(--tw-ring-offset-width))var(--tw-ring-color,currentcolor);box-shadow:var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)}.focus\\:outline-none:focus{--tw-outline-style:none;outline-style:none}dialog{--bdk-modal-default-max-width:688px;--bdk-modal-large-max-width:960px;--bdk-modal-full-screen-max-width:100vw;--bdk-modal-max-height:calc(100dvh - var(--bdk-modal-margin));--bdk-modal-margin:40px;--bdk-modal-padding-x:var(--bdk-space-5);--bdk-modal-padding-bottom:var(--bdk-space-9);--bdk-modal-transform:translate3d(0,0,0);--bdk-modal-starting-transform:translate3d(0,0,0);max-height:var(--bdk-modal-max-height);margin-top:var(--bdk-modal-margin);gap:var(--bdk-space-4);background-color:var(--bdk-snow-100);border-top-left-radius:var(--bdk-radius-3);border-top-right-radius:var(--bdk-radius-3);opacity:0;transform:var(--bdk-modal-transform);transition:display .3s allow-discrete,overlay .3s allow-discrete,transform .3s ease-out,opacity .3s ease-out;inset:auto;border:none;border-bottom-right-radius:0;border-bottom-left-radius:0;flex-direction:column;margin-inline:auto;display:none;inset-inline-start:0}dialog[open]{--bdk-modal-transform:translate3d(0,20px,0);--bdk-modal-starting-transform:translate3d(0,20px,0);opacity:1;display:flex}.modal-header-content,.modal-body,.modal-footer{padding-inline:var(--bdk-modal-padding-x)}.modal-body{padding-bottom:var(--bdk-modal-padding-bottom);flex:auto;overflow:auto}dialog:has(.modal-footer) .modal-body{padding-bottom:0}.modal-footer{padding-bottom:var(--bdk-modal-padding-bottom)}dialog::backdrop{background-color:var(--bdk-backdrop-layer,#1a1a1a80);opacity:0;width:100%;height:100%;transition:opacity .3s ease-out;position:fixed;top:0;left:0}dialog[open]::backdrop{opacity:1}@starting-style{dialog[open]{opacity:0;transform:var(--bdk-modal-starting-transform)}dialog[open]::backdrop{opacity:0}}@media (prefers-reduced-motion:reduce){dialog,dialog::backdrop{transition:none}}dialog.width-default,dialog.width-large{width:100%;max-width:100%}dialog.full-screen{border-radius:0}@media screen and (max-width:759px){dialog{bottom:0}dialog.full-screen{width:100%;max-width:var(--bdk-modal-full-screen-max-width);height:100dvh;max-height:100dvh;margin:0;inset:0}}@media screen and (min-width:760px){dialog{--bdk-modal-padding-x:var(--bdk-space-7);--bdk-modal-max-height:calc(100dvh - var(--bdk-modal-margin) - var(--bdk-modal-margin));margin-top:var(--bdk-modal-margin);margin-bottom:var(--bdk-modal-margin);border-bottom-left-radius:var(--bdk-radius-3);border-bottom-right-radius:var(--bdk-radius-3);inset:0}dialog.width-default{max-width:var(--bdk-modal-default-max-width)}dialog.width-large{max-width:var(--bdk-modal-large-max-width)}dialog.full-screen{border-radius:var(--bdk-radius-3);inset:0}}@media screen and (max-device-width:1024px) and (orientation:portrait) and (-webkit-min-device-pixel-ratio:1){dialog{margin-top:var(--bdk-modal-margin)}}@media screen and (max-device-width:1024px) and (orientation:landscape) and (-webkit-min-device-pixel-ratio:1){dialog{margin-top:var(--bdk-modal-margin)}}@property --tw-rotate-x{syntax:"*";inherits:false}@property --tw-rotate-y{syntax:"*";inherits:false}@property --tw-rotate-z{syntax:"*";inherits:false}@property --tw-skew-x{syntax:"*";inherits:false}@property --tw-skew-y{syntax:"*";inherits:false}@property --tw-border-style{syntax:"*";inherits:false;initial-value:solid}@property --tw-ease{syntax:"*";inherits:false}@property --tw-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-shadow-color{syntax:"*";inherits:false}@property --tw-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-inset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-shadow-color{syntax:"*";inherits:false}@property --tw-inset-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-ring-color{syntax:"*";inherits:false}@property --tw-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-ring-color{syntax:"*";inherits:false}@property --tw-inset-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-ring-inset{syntax:"*";inherits:false}@property --tw-ring-offset-width{syntax:"<length>";inherits:false;initial-value:0}@property --tw-ring-offset-color{syntax:"*";inherits:false;initial-value:#fff}@property --tw-ring-offset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}';
var Zt = Object.defineProperty, H = (r, t, e, o) => {
  for (var i = void 0, n = r.length - 1, a; n >= 0; n--)
    (a = r[n]) && (i = a(t, e, i) || i);
  return i && Zt(t, e, i), i;
};
class M extends _ {
  constructor() {
    super(...arguments), this.modalRef = R(), this.modalBodyRef = R(), this.modalHeaderRef = R(), this.modalFooterRef = R(), this.prefix = "", this.fullScreen = !1, this.size = "default", this.open = !1, this.ariaLabelClose = "", this.handleOutsideClick = (t) => {
      const e = t.target;
      if (e.tagName !== "DIALOG")
        return;
      const o = e.getBoundingClientRect();
      o.top <= t.clientY && t.clientY <= o.top + o.height && o.left <= t.clientX && t.clientX <= o.left + o.width || this.hide();
    };
  }
  connectedCallback() {
    super.connectedCallback(), this.handleOutsideClick = this.handleOutsideClick.bind(this), this.onClose = this.onClose.bind(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this.modalRef.value?.removeEventListener("close", this.onClose), this.modalRef.value?.removeEventListener("click", this.handleOutsideClick);
  }
  firstUpdated() {
    this.modalRef.value && this.modalRef.value.addEventListener("close", this.onClose);
  }
  updated(t) {
    super.updated(t);
  }
  get eventPrefix() {
    return this.prefix ? `${this.prefix}-` : "";
  }
  dispatchCloseEvent() {
    const t = new CustomEvent(`${this.eventPrefix}modal:close`, {
      bubbles: !0,
      composed: !0
    });
    this.dispatchEvent(t);
  }
  show() {
    document.body.inert = !0, this.open = !0, this.modalRef.value.showModal(), this.modalRef.value.ariaModal = "true", this.modalRef.value?.addEventListener("click", this.handleOutsideClick);
  }
  hide() {
    this.modalRef.value?.close();
  }
  onClose() {
    document.body.inert = !1, this.open = !1, this.modalRef.value.removeAttribute("aria-modal"), this.modalRef.value?.removeEventListener("click", this.handleOutsideClick), this.dispatchCloseEvent();
  }
  static get styles() {
    return kt`${bt(Yt)}`;
  }
  render() {
    const t = {
      "width-default": this.size === "default",
      "width-large": this.size === "large",
      "full-screen": this.fullScreen
    };
    return T`<dialog ${U(this.modalRef)} class="cursor-default p-0 transform overflow-hidden text-left ${It(
      t
    )}" role="dialog" aria-labelledby="modal-title" aria-describedby="modal-description" ?open="${this.open}">${this.renderHeader()} ${this.renderBody()} ${this.renderFooter()}</dialog>`;
  }
  renderHeader() {
    return T`<div id="modal-header" class="modal-header w-full relative" ${U(this.modalHeaderRef)}><div class="flex justify-between items-center m-1"><div><slot name="header-start"></slot></div><div><slot name="header-center"></slot></div><button @click="${this.hide}" type="button" class="bdk-tw-button-icon" variant="just-icon" size="small" aria-label="${this.ariaLabelClose}"><span role="presentation">${Kt(Xt)}</span></button></div><div class="modal-header-content"><div class="bdk-tw-heading-m text-coal" id="modal-title"><slot name="title"></slot></div><div id="modal-description" class="sr-only"><slot name="description"></slot></div></div></div>`;
  }
  renderBody() {
    return T`<div id="modal-body" class="modal-body" ${U(this.modalBodyRef)}><slot></slot></div>`;
  }
  renderFooter() {
    if (this.querySelector('[slot="footer"]') !== null)
      return T`<div id="modal-footer" class="modal-footer" ${U(this.modalFooterRef)}><slot name="footer"></slot></div>`;
  }
}
H([
  P({ type: String })
], M.prototype, "prefix");
H([
  P({ type: Boolean, reflect: !0 })
], M.prototype, "fullScreen");
H([
  P({ type: String, reflect: !0 })
], M.prototype, "size");
H([
  P({ type: Boolean, reflect: !0 })
], M.prototype, "open");
H([
  P({ type: String, reflect: !0 })
], M.prototype, "ariaLabelClose");
export {
  M as Modal
};
